import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useCompOff, useRaiseCompOff } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

/** Time back for a day worked on a public holiday (HRX-02 → HRX-04).
 *
 * The customer's continuous lines do not stop for Parents' Day, and the crew that keeps them running is owed a day.
 * Nothing could show that before: `mark_auto_attendance_on_holidays` is off by default on every shift type, so
 * `should_mark_attendance` returns False for a holiday and the punches never become Attendance at all — a
 * claim with no evidence behind it.
 *
 * Overtime on an ordinary working day is deliberately absent. That is an overtime question; paying for the
 * same hour twice, once in hours and once in days, is how a comp-off scheme becomes unaffordable. */
export default function CompOff({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useCompOff(company || undefined);
  const raise = useRaiseCompOff();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-leave-compoff>
      <p className="max-w-4xl text-sm text-brand-grey">{t("lv.compHint", { from: d.from, to: d.to ?? "", holidays: d.holidays })}</p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-leave-comp-strip>
        <Stat label={t("lv.workedHolidays")} value={d.rows.length} hint={t("lv.workedHolidaysHint")} />
        <Stat label={t("lv.unclaimed")} value={d.unclaimed} cls={d.unclaimed ? "text-status-amber" : ""} />
        <Stat label={t("lv.daysDue")} value={d.days_due} cls={d.days_due ? "text-status-amber" : ""} hint={t("lv.daysDueHint")} />
        <Stat label={t("lv.holidaysInList")} value={d.holidays} hint={t("lv.holidaysHint")} />
      </div>

      <SectionCard title={t("lv.compTitle")} aside={t("lv.compN", { n: d.rows.length })}>
        {d.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-leave-comp-empty>{t("lv.compEmpty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2">{t("lv.workedOn")}</th>
                <th className="px-2 py-2">{t("hr.gate.shift")}</th>
                <th className="px-2 py-2 text-right">{t("lab.hours")}</th>
                <th className="px-2 py-2 text-right">{t("lv.timeBack")}</th>
                <th className="px-4 py-2">{t("ops.status")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={`${r.employee}-${r.attendance_date}`} className="border-t border-brand-grey-light"
                  data-leave-comp-row={r.employee}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">{r.company}</div>
                  </td>
                  <td className="px-2 py-2 tabular-nums">{r.attendance_date}</td>
                  <td className="px-2 py-2 text-[11px] text-brand-grey">{r.shift}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.working_hours.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-semibold tabular-nums">{r.days}</td>
                  <td className="px-4 py-2">
                    {r.claimed ? (
                      <span className="rounded bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-status-green">{t("lv.claimed")}</span>
                    ) : (
                      <button onClick={() => raise.mutate({ employee: r.employee, work_date: r.attendance_date, half_day: r.days < 1 ? 1 : 0 })}
                        disabled={raise.isPending} data-leave-comp-raise={r.employee}
                        className="rounded bg-brand-primary px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                        {t("lv.raiseClaim")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {raise.isError && <ErrorBox error={raise.error} />}
    </div>
  );
}
